import React, { useState } from "react";
import { Box, Typography, Button, Popover, Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

const ReportTable = ({ title, columns, data, loading, filtersNode, onDownload }) => {
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);

  const handleColumnsClick = (event) => setAnchorEl(event.currentTarget);
  const handleColumnsClose = () => setAnchorEl(null);

  const toggleColumn = (field) => {
    setColumnVisibilityModel((prev) => ({
      ...prev,
      [field]: prev[field] === false ? true : false,
    }));
  };

  const open = Boolean(anchorEl);
  const responsiveColumns = columns.map((col) => ({
    minWidth: col.minWidth || col.width || 160,
    flex: col.flex || 1,
    ...col,
  }));
  const exportFields = columns.filter((col) => columnVisibilityModel[col.field] !== false).map((col) => col.exportField || col.field);

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", xl: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", xl: "flex-start" },
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h3" sx={{ alignSelf: { xs: "flex-start", xl: "center" } }}>
          {title}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
            alignContent: "center",
            justifyContent: { xs: "stretch", md: "flex-end" },
            "> .MuiFormControl-root, > .MuiTextField-root": {
              minWidth: { xs: "100%", sm: 180 },
              flex: { xs: "1 1 100%", sm: "0 1 180px" },
            },
            "& .report-table-action": {
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
            },
          }}
        >
          {filtersNode}

          <Button variant="outlined" sx={{ minWidth: 40, p: 1 }} className="report-table-action" onClick={handleColumnsClick}>
            <ViewColumnOutlinedIcon />
          </Button>

          <Button
            variant="outlined"
            startIcon={<DownloadOutlinedIcon />}
            onClick={() => onDownload(exportFields)}
            disabled={exportFields.length === 0}
            sx={{ textTransform: "none" }}
            className="report-table-action"
          >
            Download Excel
          </Button>
        </Box>
      </Box>

      {/* Columns Popover */}
      <Popover open={open} anchorEl={anchorEl} onClose={handleColumnsClose} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <Box p={2} sx={{ minWidth: 200, maxHeight: 400, overflowY: "auto" }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "gray" }}>
            Columns
          </Typography>
          <FormGroup>
            {columns.map((col) => {
              const checked = columnVisibilityModel[col.field] !== false;
              return <FormControlLabel key={col.field} control={<Checkbox checked={checked} onChange={() => toggleColumn(col.field)} />} label={col.headerName} />;
            })}
          </FormGroup>
        </Box>
      </Popover>

      <Box
        sx={{
          height: { xs: 520, md: 600 },
          width: "100%",
          background: "#fff",
          border: "1px solid #ddd",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <DataGrid
          disableColumnFilter
          disableColumnMenu
          getRowHeight={() => "auto"}
          sx={{
            border: 0,
            minWidth: 0,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f7fb",
              fontWeight: "bold",
              borderBottom: "1px solid #ddd",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #eee",
              py: 1,
            },
            "& .MuiDataGrid-columnSeparator": {
              visibility: "visible",
            },
            // Custom pagination styles
            ".MuiTablePagination-displayedRows": {
              marginTop: "1em",
              marginBottom: "1em",
            },
            ".MuiTablePagination-selectLabel": {
              marginTop: "1em",
              marginBottom: "1em",
            },
          }}
          rows={data}
          columns={responsiveColumns}
          disableRowSelectionOnClick
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
          loading={loading}
          getRowId={(row) => row.id || row.reportId || row.clientProfileId || row.clientNumber || JSON.stringify(row)}
        />
      </Box>
    </Box>
  );
};

export default ReportTable;
